import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import {
  ExcludedLeaf,
  PayloadDiff,
  RequestPair,
  SessionDiff,
  SessionDiffService,
} from '../../services/session-diff.service';

/**
 * Read-only comparison view at /diff?a=&b=.
 *
 * The page is deliberately deep-linkable: the only state lives in the
 * URL so an operator can paste the link into a Linear ticket and the
 * recipient lands on the same comparison. All heavy lifting is in
 * SessionDiffService — this component is the layout + interaction
 * shell.
 */
@Component({
  selector: 'app-diff-view',
  imports: [CommonModule, RouterLink, NgxSpinnerComponent],
  templateUrl: './diff-view.html',
})
export class DiffView implements OnInit {
  /** Session A value from the URL. */
  sessionAValue: string = '';
  /** Session B value from the URL. */
  sessionBValue: string = '';

  /** Final computed diff. Null while loading, or when an error fired. */
  diff: SessionDiff | null = null;

  /** Inline error message — shown in place of the cards if non-empty. */
  errorMsg: string = '';

  /** Whether a diff request is currently in flight (drives the localized indicator). */
  loading = false;

  /** Whether the "excluded fields" drawer is open. */
  showExcluded = false;

  /** Cached aggregate of all excluded leaves across all pairs (for the drawer). */
  allExcluded: { pairLabel: string; leaf: ExcludedLeaf; payloadName: string }[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    protected readonly location: Location,
    private readonly diffService: SessionDiffService,
    private readonly spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.sessionAValue = (params.get('a') ?? '').trim();
      this.sessionBValue = (params.get('b') ?? '').trim();
      if (!this.sessionAValue || !this.sessionBValue) {
        this.errorMsg = 'Both ?a=<session> and ?b=<session> are required.';
        return;
      }
      if (this.sessionAValue === this.sessionBValue) {
        this.errorMsg = 'Session A and B are the same — pick a different B.';
        return;
      }
      this.runDiff();
    });
  }

  /** Refire the diff (used after Swap, and by the inline "Try again" button). */
  async runDiff(): Promise<void> {
    this.diff = null;
    this.errorMsg = '';
    this.allExcluded = [];
    this.loading = true;
    this.spinner.show();
    try {
      const result = await this.diffService.diffSessions(this.sessionAValue, this.sessionBValue);
      this.diff = result;
      this.allExcluded = this.collectExcluded(result);
    } catch (e) {
      // Single error channel: the inline box below (which hosts the retry
      // button). Intentionally no toastr here to avoid a duplicate report.
      this.errorMsg = e instanceof Error ? e.message : 'Failed to compute diff';
    } finally {
      this.loading = false;
      this.spinner.hide();
    }
  }

  /** Swap A ↔ B via URL update (which retriggers ngOnInit's subscription). */
  swap(): void {
    this.router.navigate(['/diff'], {
      queryParams: { a: this.sessionBValue, b: this.sessionAValue },
    });
  }

  /** Download the computed SessionDiff as a JSON blob. */
  exportJson(): void {
    if (!this.diff) return;
    const json = JSON.stringify(this.diff, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diff_${this.sessionAValue}_vs_${this.sessionBValue}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Format a value for inline display in the diff row. */
  formatValue(v: unknown): string {
    if (v === undefined) return '∅';
    if (v === null) return 'null';
    if (typeof v === 'string') return `"${v}"`;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      const s = JSON.stringify(v);
      return s.length > 80 ? s.slice(0, 77) + '…' : s;
    } catch {
      return String(v);
    }
  }

  /** "AirShopping · IB" style label for a pair card. */
  pairLabel(pair: RequestPair): string {
    const r = pair.a ?? pair.b;
    if (!r) return '(unknown)';
    const provider = r.provider || r.provider_method || '';
    return provider ? `${r.method} · ${provider}` : r.method;
  }

  /** A.seq=N ↔ B.seq=M */
  pairPositionHint(pair: RequestPair, index: number): string {
    if (pair.status === 'paired') {
      return `paired · pos ${index}`;
    }
    return pair.status === 'only_a'
      ? 'only in A — no B counterpart'
      : 'only in B — no A counterpart';
  }

  /** Build the queryParams object for the /search deep-link on a diff leaf. */
  diffOpenQueryParams(
    pair: RequestPair,
    side: 'a' | 'b',
    payload: PayloadDiff,
    path: string
  ): { [k: string]: string } {
    const req = side === 'a' ? pair.a : pair.b;
    const sessionValue = side === 'a' ? this.sessionAValue : this.sessionBValue;
    return {
      q: sessionValue,
      req: String(req?.id ?? ''),
      payload: payload.messageName,
      path,
      from: 'diff',
      other: side === 'a' ? this.sessionBValue : this.sessionAValue,
    };
  }

  /** Concatenate every excluded leaf across all pairs for the drawer. */
  private collectExcluded(diff: SessionDiff): {
    pairLabel: string;
    leaf: ExcludedLeaf;
    payloadName: string;
  }[] {
    const out: { pairLabel: string; leaf: ExcludedLeaf; payloadName: string }[] = [];
    for (const pair of diff.pairs) {
      if (pair.status !== 'paired' || !pair.payloadDiffs) continue;
      const label = this.pairLabel(pair);
      for (const p of pair.payloadDiffs) {
        for (const ex of p.excluded) {
          out.push({ pairLabel: label, leaf: ex, payloadName: p.messageName });
        }
      }
    }
    return out;
  }

  /** Human-readable session timestamp (avoids importing the FE pipe here). */
  sessionMeta(side: 'a' | 'b'): string {
    if (!this.diff) return '';
    const s = side === 'a' ? this.diff.sessionA : this.diff.sessionB;
    if (!s) return '';
    const created = s.created_at ? new Date(s.created_at).toLocaleString() : '';
    const count = s.requests?.length ?? 0;
    return `${created} · ${count} request${count === 1 ? '' : 's'}`;
  }

  /** "1.2 s" / "340 ms" for the summary band. */
  computeTime(): string {
    if (!this.diff) return '';
    const ms = this.diff.computedAtMs;
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
  }

  /** Total number of paired requests, for the summary band. */
  pairedCount(): number {
    return (this.diff?.pairedWithDiffs ?? 0) + (this.diff?.pairedIdentical ?? 0);
  }
}
